import { GeneralResourceDetail } from '@cibo/core'
import { Loading } from '@cibo/profile'
import {
  ClearOrSaveButtonsFormik,
  NumbersOnlyFormikTextField,
  ResourceDetailFeatureTaskEditorProps,
} from '@cibo/ui'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Collapse,
  FormHelperText,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Field, FieldArray, Form, Formik, useFormikContext } from 'formik'
import { mergeAll } from 'ramda'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { useProgramEngagementDetails, useUpdateProgramEngagementDetails } from '../../../../queries'
import { EqipFarmNumbersDetail } from './types'

type EqipFarmNumbersFormValues = {
  farmNumbers: Array<{ farmNumber: string; needsValidation?: boolean }>
}

const detailValueFromFormValues = (values: EqipFarmNumbersFormValues) => {
  return values.farmNumbers.map(({ farmNumber }) => farmNumber)
}

const formValuesFromDetailValue = (detailValue: EqipFarmNumbersDetail) => {
  return { farmNumbers: detailValue.value?.map(farmNumber => ({ farmNumber })) || [] }
}

const firstDetail = (details?: GeneralResourceDetail[]) => {
  if (!details || details.length === 0) return undefined
  return details[0]
}

export const EqipFarmNumbersTaskEditorForm = ({
  detailRequirements,
  engagementId,
  onClear,
}: EqipFarmNumbersTaskEditorProps & { onClear(): void }) => {
  const { t } = useTranslation('@cibo/programs/EqipFarmNumbersTaskEditor')
  const details = useProgramEngagementDetails({ engagementId, detailRequirements })
  const detail = firstDetail(details.data)

  const ciboDetailValue: EqipFarmNumbersDetail['value'] | undefined =
    detail?.source === 'cibo' ? (detail as EqipFarmNumbersDetail).value : undefined

  const { values, isValid, isValidating, touched, validateForm, setFieldValue } =
    useFormikContext<EqipFarmNumbersFormValues>()

  useEffect(() => {
    // arrayHelpers.push() triggers a change in `values` before validation has been rerun.
    // The below will skip sending the new, invalid value to the server when we push() a
    // new member to the array.
    if (values.farmNumbers.find(farmNumber => farmNumber.needsValidation)) {
      setFieldValue('farmNumbers', values.farmNumbers)
      return
    }
  }, [values, isValid, touched, isValidating, setFieldValue])

  const disabled = detail?.immutable

  const hasUserValue = detail?.source === 'user' && detail?.value

  return (
    <Form>
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Stack>
            <Typography>{t('cta')}</Typography>

            {detail?.source === 'cibo' && (
              <Box>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setFieldValue(
                      'farmNumbers',
                      ciboDetailValue?.map(farmNumber => ({ farmNumber }))
                    )
                    validateForm()
                  }}
                >
                  {t('useFieldFarmNumbers', {
                    count: ciboDetailValue?.length,
                    farmNumbers: ciboDetailValue?.join(', '),
                  })}
                </Button>
              </Box>
            )}
          </Stack>

          <FieldArray
            name={`farmNumbers`}
            render={arrayHelpers => (
              <>
                <TableContainer>
                  <Table>
                    <TableBody>
                      {(values.farmNumbers || []).map((_farmNumber, index) => (
                        <TableRow key={index} sx={{ border: 'none' }}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Field
                              id={`farmNumbers.${index}.farmNumber`}
                              name={`farmNumbers.${index}.farmNumber`}
                              component={NumbersOnlyFormikTextField}
                              InputProps={{
                                inputProps: {
                                  'data-testid': `farmNumbers.${index}.farmNumber`,
                                  autoComplete: 'off',
                                },
                              }}
                              maxLength={255}
                              placeholder={t('enterFarmNumber')}
                              disabled={disabled}
                              fullWidth={true}
                            />
                          </TableCell>

                          <TableCell>
                            <Tooltip title={t('remove')}>
                              <IconButton onClick={() => arrayHelpers.remove(index)}>
                                <CloseIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Button
                  startIcon={<AddIcon />}
                  onClick={async () => {
                    arrayHelpers.push({ farmNumber: '', needsValidation: true })
                    validateForm()
                  }}
                  disabled={disabled}
                >
                  {t('addNumber')}
                </Button>
              </>
            )}
          />
        </Stack>

        <ClearOrSaveButtonsFormik
          hasValidDetail={!!hasUserValue}
          disabled={disabled}
          onClear={onClear}
          testid="farmNumbers"
        />
      </Stack>
    </Form>
  )
}

const DEFAULT_FORM_VALUES = {
  farmNumbers: [],
}

type EqipFarmNumbersTaskEditorProps = Omit<
  ResourceDetailFeatureTaskEditorProps<EqipFarmNumbersDetail>,
  'engagement' | 'resourceIds' | 'ownerName' | 'userRole' | 'task'
>

export const EqipFarmNumbersTaskEditor = (props: EqipFarmNumbersTaskEditorProps) => {
  const { t } = useTranslation('@cibo/programs/EqipFarmNumbersTaskEditor')

  const { onUpdating, onError, onSuccess, engagementId, detailRequirements } = props

  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const details = useProgramEngagementDetails<EqipFarmNumbersDetail>({
    engagementId,
    detailRequirements: detailRequirements,
  })
  const detail = details?.data?.[0]
  const detailValues = detail ? formValuesFromDetailValue(detail) : { farmNumbers: [] }

  const initialValues = useMemo(() => {
    if (!detailValues) return DEFAULT_FORM_VALUES
    return mergeAll([DEFAULT_FORM_VALUES, detail?.source === 'user' ? detailValues : {}])
  }, [detail, detailValues])

  const schema = useMemo(
    () =>
      yup.object({
        farmNumbers: yup
          .array()
          .min(1, t('oneRequired'))
          .of(
            yup.object({
              farmNumber: yup.string().required(t('farmNumberRequired')),
            })
          )
          .test('unique', 'Duplicate value found', function (value) {
            if (!value) return true // let Yup handle required errors

            const seen = new Set<string>()

            for (let i = 0; i < value.length; i++) {
              const item = value[i]

              if (seen.has(item.farmNumber)) {
                return this.createError({
                  path: `farmNumbers[${i}].farmNumber`,
                  message: t('pleaseRemoveDuplicate'),
                })
              }

              seen.add(item.farmNumber)
            }

            return true
          }),
      }),
    []
  )

  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const updateEngagementDetail = useUpdateProgramEngagementDetails<EqipFarmNumbersDetail>()

  const onSubmit = useCallback((values: any, { setSubmitting }: any) => {
    const newDetailValue = detailValueFromFormValues(values)

    onUpdating?.()
    updateEngagementDetail
      .mutateAsync({
        engagementId,
        details: [
          {
            traitId: 'eqipFarmNumbers',
            value: newDetailValue as EqipFarmNumbersDetail['value'],
          },
        ],
      })
      .then(onSuccess)
      .catch(onError)
      .finally(() => setSubmitting(false))
  }, [])

  const onClear = useCallback(() => {
    onUpdating?.()
    updateEngagementDetail
      .mutateAsync({
        engagementId,
        details: [{ traitId: 'eqipFarmNumbers' as const }],
      })
      .then(() => {
        onSuccess?.()
        details.refetch()
      })
      .catch(onError)
  }, [])

  if (details.isPending) return <Loading />

  return (
    <Formik<EqipFarmNumbersFormValues>
      // @ts-ignore How to allow empty strings for these initial values?
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
      validateOnMount
      enableReinitialize
    >
      <>
        <EqipFarmNumbersTaskEditorForm {...props} onClear={onClear} />

        <Collapse in={updateEngagementDetail.isError}>
          <FormHelperText>{t('error')}</FormHelperText>
        </Collapse>
      </>
    </Formik>
  )
}
